import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("This link is missing its token.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      setUser({ ...data, isAuthenticated: true });
      setDone(true);
      const role = data?.role || data?.user?.role;
      const dest =
        role === "partner" || role === "insurance_partner" ? "/partner"
        : role === "vet" ? "/vet"
        : role === "fulfillment_staff" ? "/fulfillment"
        : role === "admin" ? "/admin"
        : "/dashboard";
      setTimeout(() => navigate(dest), 1400);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-paw-border rounded-2xl p-8 text-center" data-testid="set-password-success">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-emerald-500" strokeWidth={2} />
          </div>
          <h1 className="font-outfit font-bold text-2xl text-paw-text mb-2">Password set!</h1>
          <p className="font-manrope text-sm text-paw-text-muted">Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full" data-testid="set-password-page">
        <div className="text-center mb-7">
          <img src="/logo-light-256.png" alt="PawLinx" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="font-outfit font-bold text-[26px] text-paw-text tracking-tight">Welcome to PawLinx</h1>
          <p className="font-manrope text-sm text-paw-text-muted mt-1.5">Set a password so you can track your order, view invoices, and reorder in one click.</p>
        </div>

        <form onSubmit={onSubmit} className="bg-white border border-paw-border rounded-2xl p-6 space-y-4">
          <div>
            <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted block mb-1.5">New password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-paw-text-muted" strokeWidth={1.5} />
              <input type="password" autoFocus required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 pl-10 pr-4 rounded-xl bg-paw-bg border border-paw-border text-paw-text text-sm font-manrope focus:outline-none focus:border-paw-primary"
                placeholder="At least 8 characters" data-testid="set-password-input" />
            </div>
          </div>
          <div>
            <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted block mb-1.5">Confirm password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-paw-text-muted" strokeWidth={1.5} />
              <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full h-11 pl-10 pr-4 rounded-xl bg-paw-bg border border-paw-border text-paw-text text-sm font-manrope focus:outline-none focus:border-paw-primary"
                placeholder="Re-enter password" data-testid="set-password-confirm" />
            </div>
          </div>
          <button type="submit" disabled={submitting || !token} className="w-full h-11 bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-50 rounded-xl font-manrope font-semibold text-sm text-white flex items-center justify-center gap-2 transition-colors" data-testid="set-password-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set password & continue"}
          </button>
          <p className="text-center">
            <Link to="/login" className="font-manrope text-xs text-paw-text-muted hover:text-paw-primary">Already have an account? Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
